import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarClock, Check, ClipboardCopy, Loader2, MessageCircle, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const emptyForm = { patientId: '', dueDate: '', time: '', reason: '', whatsappReminderEnabled: false };
const labels = {
  not_configured: 'Provider not configured',
  simulated: 'Simulated (development)',
  provider_accepted: 'Sent (provider accepted)',
  delivery_confirmed: 'Patient confirmed',
  invalid_number: 'Failed',
  provider_rejected: 'Failed',
  provider_transient_failure: 'Failed',
  calling_provider: 'Pending',
  manual_pending: 'Send required',
  manual_handoff: 'Manual handoff recorded',
};

function formatDate(value) {
  if (!value) return '-';
  return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    .format(new Date(`${value}T00:00:00`));
}
function formatTime(value) { return value ? String(value).slice(0, 5) : '-'; }
function daysUntil(value) { const due = new Date(`${value}T00:00:00+05:30`); const todayKey = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()); return Math.round((due - new Date(`${todayKey}T00:00:00+05:30`)) / 86400000); }

export default function FollowUps() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('upcoming');
  const [rows, setRows] = useState([]);
  const [patients, setPatients] = useState([]);
  const [availability, setAvailability] = useState({ status: 'requires_configuration' });
  const [form, setForm] = useState({ ...emptyForm, patientId: searchParams.get('patientId') || '' });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const reminderConfigured = availability.status === 'available';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const patientFilter = searchParams.get('patientId');
      const [listResponse, patientResponse, availabilityResponse] = await Promise.all([
        api.get(`/follow-ups/${tab}`, { params: { page, limit: 20, ...(patientFilter ? { patientId: patientFilter } : {}) } }),
        api.get('/patients/all', { params: { page: 1, limit: 100 } }),
        api.get('/follow-ups/availability'),
      ]);
      setRows(listResponse.data?.items || []);
      setPagination(listResponse.data?.pagination || { totalPages: 1, total: 0 });
      setPatients(patientResponse.data?.patients || patientResponse.data?.items || patientResponse.data || []);
      setAvailability(availabilityResponse.data || { status: 'requires_configuration' });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Unable to load follow-ups');
    } finally {
      setLoading(false);
    }
  }, [page, searchParams, tab]);

  useEffect(() => { load(); }, [load]);

  const selectedPatient = useMemo(
    () => patients.find((patient) => String(patient.id) === String(form.patientId)),
    [form.patientId, patients]
  );

  const create = async (event) => {
    event.preventDefault();
    setSaving('create');
    try {
      await api.post('/follow-ups', form);
      toast.success(`Follow-up scheduled for ${selectedPatient?.name || 'patient'}. Use Send schedule to notify them on WhatsApp.`);
      setForm({ ...emptyForm, patientId: searchParams.get('patientId') || '' });
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Unable to schedule follow-up');
    } finally { setSaving(''); }
  };

  const act = async (key, request, message) => {
    setSaving(key);
    try {
      await request();
      toast.success(message);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Unable to update follow-up');
    } finally { setSaving(''); }
  };

  const reschedule = (row) => {
    const dueDate = window.prompt('New follow-up date (YYYY-MM-DD)', row.dueDate);
    if (!dueDate) return;
    const time = window.prompt('New follow-up time (HH:MM)', formatTime(row.Appointment?.time));
    if (time) act(`edit-${row.id}`, () => api.patch(`/follow-ups/${row.id}`, { dueDate, time }), 'Follow-up rescheduled.');
  };
  const cancel = (row) => {
    const cancelReason = window.prompt('Why is this follow-up being cancelled?');
    if (cancelReason?.trim()) act(`cancel-${row.id}`, () => api.post(`/follow-ups/${row.id}/cancel`, { cancelReason }), 'Follow-up cancelled.');
  };
  const stopReminders = (row) => {
    const reason = window.prompt('Why are automated reminders being stopped?');
    if (reason?.trim()) act(`stop-${row.id}`, () => api.post(`/follow-ups/${row.id}/stop-reminders`, { reason }), 'Automated reminders stopped.');
  };
  const sendWhatsApp = async (row) => {
    const kind = row.isMissed ? 'missed' : (daysUntil(row.dueDate) <= 1 ? 'reminder' : 'scheduled');
    setSaving(`message-${row.id}`);
    try {
      const response = await api.post(`/follow-ups/${row.id}/prepare-whatsapp`, { kind });
      if (response.data?.deliveryMode === 'provider') {
        if (response.data.status === 'Sent') toast.success('WhatsApp message sent to the configured provider.');
        else if (response.data.deliveryState === 'duplicate_prevented') toast(response.data.message);
        else toast.error(response.data.message || 'WhatsApp message failed.');
      } else {
        if (!response.data?.whatsappUrl) throw new Error('WhatsApp link was not created');
        window.open(response.data.whatsappUrl, '_blank', 'noopener,noreferrer');
        toast.success('Message opened in WhatsApp. Manual handoff recorded.');
      }
      await load();
    } catch (error) { toast.error(error.response?.data?.error || error.message || 'Unable to prepare WhatsApp message'); }
    finally { setSaving(''); }
  };
  const copyPatientLink = async (row) => {
    try { await navigator.clipboard.writeText(row.managementUrl); toast.success('Patient follow-up link copied.'); }
    catch (_error) { toast.error('Unable to copy patient link'); }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="rounded-[28px] bg-gradient-to-br from-[#0c1f3d] to-[#0d9488] p-6 text-white shadow-xl">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-teal-100">Patient care</p>
          <h1 className="mt-2 text-3xl font-semibold">Follow-up reminders</h1>
          <p className="mt-2 max-w-2xl text-sm text-teal-50">Schedule patient follow-ups, monitor overdue cases, and see the honest WhatsApp delivery state.</p>
        </div>

        <form onSubmit={create} className="grid gap-4 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm md:grid-cols-2 xl:grid-cols-6">
          <select required value={form.patientId} onChange={(event) => setForm({ ...form, patientId: event.target.value })} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm">
            <option value="">Select patient</option>
            {patients.map((patient) => <option key={patient.id} value={patient.id}>{patient.name} ({patient.patientId || patient.id})</option>)}
          </select>
          <input required type="date" value={form.dueDate} onChange={(event) => setForm({ ...form, dueDate: event.target.value })} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <input required aria-label="Follow-up time" type="time" value={form.time} onChange={(event) => setForm({ ...form, time: event.target.value })} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <input value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} placeholder="Reason (optional)" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <label title={reminderConfigured ? 'Send through the configured WhatsApp provider' : 'Automatic WhatsApp delivery is not configured'} className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm font-semibold ${reminderConfigured ? 'border-teal-200 text-teal-800' : 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'}`}>
            <input type="checkbox" disabled={!reminderConfigured} checked={form.whatsappReminderEnabled} onChange={(event) => setForm({ ...form, whatsappReminderEnabled: event.target.checked })} />
            Automatic day-before reminder
          </label>
          <button disabled={saving === 'create'} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-teal-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60">
            {saving === 'create' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Schedule
          </button>
          {!reminderConfigured && <p className="text-xs text-amber-700 md:col-span-2 xl:col-span-6">Automatic WhatsApp delivery needs a configured provider. Manual Send on WhatsApp buttons remain available.</p>}
        </form>

        <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-2">
              {['upcoming', 'overdue'].map((item) => <button key={item} onClick={() => { setTab(item); setPage(1); }} className={`rounded-full px-4 py-2 text-sm font-semibold capitalize ${tab === item ? 'bg-[#0c1f3d] text-white' : 'bg-slate-100 text-slate-600'}`}>{item}</button>)}
            </div>
            <span className="text-sm text-slate-500">{pagination.total} follow-ups</span>
          </div>
          {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div> : rows.length === 0 ? (
            <div className="py-16 text-center text-slate-500"><CalendarClock className="mx-auto mb-3 h-8 w-8" /><p>No {tab} follow-ups.</p></div>
          ) : <div className="mt-5 space-y-3">{rows.map((row) => (
            <article key={row.id} className={`rounded-2xl border p-4 ${row.isMissed ? 'border-rose-300 bg-rose-50/60' : 'border-slate-200'}`}>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="font-semibold text-slate-900">{row.Patient?.name || `Patient ${row.patientId}`}</p>
                  <p className="mt-1 text-sm text-slate-500">{row.Patient?.phone || '-'} · {formatDate(row.dueDate)} at {formatTime(row.Appointment?.time)}</p>
                  <p className="mt-1 text-sm text-slate-500">Assigned doctor: {row.Appointment?.doctorAssigned || 'Not assigned'} · Confirmation: {row.confirmationState || 'pending'}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold capitalize text-slate-600">{row.status.replaceAll('_', ' ')}</span>
                    {row.isMissed && <span className="rounded-full bg-rose-100 px-2.5 py-1 text-xs font-semibold text-rose-700">Missed appointment</span>}
                    {row.automationStoppedAt && <span className="rounded-full bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700">Reminders stopped</span>}
                    {row.whatsappReminderEnabled && <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700"><MessageCircle className="h-3 w-3" /> {labels[row.lastDeliveryState] || (row.status === 'reminder_sent' ? 'Sent to provider' : 'Reminder enabled')}</span>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => copyPatientLink(row)} disabled={!row.managementUrl} className="inline-flex items-center gap-1 rounded-full border border-teal-200 px-3 py-2 text-xs font-semibold text-teal-800 disabled:opacity-40"><ClipboardCopy className="h-3 w-3" /> Copy Patient Link</button>
                  <button onClick={() => sendWhatsApp(row)} disabled={Boolean(saving) || !row.manualSendRequired} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:bg-slate-300"><MessageCircle className="h-3 w-3" /> {saving === `message-${row.id}` ? 'Preparing...' : row.manualSendRequired ? 'SEND' : 'SENT / HANDED OFF'}</button>
                  <button onClick={() => reschedule(row)} disabled={Boolean(saving)} className="rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">Reschedule</button>
                  <button onClick={() => act(`complete-${row.id}`, () => api.post(`/follow-ups/${row.id}/complete`), 'Follow-up completed.')} disabled={Boolean(saving)} className="inline-flex items-center gap-1 rounded-full bg-teal-600 px-3 py-2 text-xs font-semibold text-white"><Check className="h-3 w-3" /> Complete</button>
                  {!row.automationStoppedAt && <button onClick={() => stopReminders(row)} disabled={Boolean(saving)} className="rounded-full border border-amber-300 px-3 py-2 text-xs font-semibold text-amber-800">Stop reminders</button>}
                  <button onClick={() => cancel(row)} disabled={Boolean(saving)} className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-700"><X className="h-3 w-3" /> Cancel</button>
                </div>
              </div>
            </article>
          ))}</div>}
          <div className="mt-5 flex justify-end gap-2">
            <button disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded-full border px-3 py-2 text-xs disabled:opacity-40">Previous</button>
            <span className="px-3 py-2 text-xs text-slate-500">Page {page} of {pagination.totalPages}</span>
            <button disabled={page >= pagination.totalPages} onClick={() => setPage((value) => value + 1)} className="rounded-full border px-3 py-2 text-xs disabled:opacity-40">Next</button>
          </div>
        </section>
      </div>
    </div>
  );
}
